import { themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { api, apiUrl } from "../../shared/services/service";
import { Bq7ContainerM, initialBq6Container, mapSqlToBq7ContainerForm } from "../models/bq7-container.model";
import { BqCommVis } from "../models/bq-forms.models";
import { bqF7ContainerAtom } from "../state/baltic-form.atoms";
import { useAtom, useAtomValue } from "jotai";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { twMerge } from "tailwind-merge";

interface SectionSevenProps {
  isCompletedForm: boolean;
  cacheInitialForm: (form: any) => void;
  readonly: boolean;
}

export const Bq7Container: React.FC<SectionSevenProps> = ({ isCompletedForm, cacheInitialForm, readonly }) => {
  const theme = useAtomValue(themeAtom);
  const [storedForm, setStoredForm] = useAtom(bqF7ContainerAtom);

  const { imoNumber, vesselName } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState<Bq7ContainerM>(initialBq6Container);
  const [commentsVisible, setCommentsVisible] = useState<BqCommVis>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  function toggleComments(inputId: string) {
    const newComms = { ...commentsVisible };
    newComms[inputId] = !commentsVisible[inputId];

    setCommentsVisible(newComms);
  }

  function isCommentsVisible(inputId: string): boolean {
    return commentsVisible[inputId];
  }

  // Get
  useEffect(() => {
    const _form = storedForm ?? {};
    const storageisEmpty = Object.keys(_form).length === 0;

    if (isCompletedForm) {
      fetchData();
    } else {
      if (storageisEmpty) {
        fetchData();
      } else {
        setForm(_form);
        cacheInitialForm(_form);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedForm]);

  // Storage
  useEffect(() => {
    if (!isLoading) {
      setStoredForm(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const result = await api.get(`${apiUrl}/bq_Container`, {
        params: {
          imo_no: imoNumber,
          vessel_name: vesselName,
        },
      });
      const _formSql = result.data[0];
      const _form = mapSqlToBq7ContainerForm(_formSql);
      setForm(_form);
      setStoredForm(_form);
      cacheInitialForm(_form);
    } catch (error: unknown) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorSize="lg" loadingIndicatorClassName="text-green">
      <div
        className={twMerge(
          "p-2 md:p-4",
          "[&_input]:border",
          "[&_select]:text-sm",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
          theme.balticQuestionnaire.inputsBg,
          theme.balticQuestionnaire.inputsText,
          theme.balticQuestionnaire.inputsBorder,
          theme.balticQuestionnaire.inputsFocusBorder,
          theme.balticQuestionnaire.inputsFocusRing,
          theme.balticQuestionnaire.selectsFocusBorder,
          theme.balticQuestionnaire.selectsFocusRing,
          theme.balticQuestionnaire.selectsBg,
          theme.balticQuestionnaire.selectsBorder,
          theme.balticQuestionnaire.textsBg,
          theme.balticQuestionnaire.textsText,
          theme.balticQuestionnaire.textsBorder,
          theme.balticQuestionnaire.selectsScroller,
          theme.balticQuestionnaire.textAreasFocusBorder,
          theme.balticQuestionnaire.textAreasFocusRing,
        )}
      >
        <h1 className="custom-heading mb-4">Container Bulkers / Multi Purpose (only if applicable)</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Capacity in direct stow of TEU/FEU basis empty tanks:</label>
              <input
                disabled={readonly}
                type="number"
                name="capacityTEUFEUEmpty"
                value={form.capacityTEUFEUEmpty}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">Capacity in direct stow of TEU/FEU basis full tanks:</label>
              <input
                disabled={readonly}
                type="number"
                name="capacityTEUFEUFull"
                value={form.capacityTEUFEUFull}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          {/* Are all containers within reach of vessel's gear?: */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Are all containers within reach of vessel's gear?:</label>
              <div className="mt-1 flex space-x-2">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="containersWithinReach"
                    value="yes"
                    checked={form.containersWithinReach === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="containersWithinReach"
                    value="no"
                    checked={form.containersWithinReach === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="containersWithinReach"
                    value="n/a"
                    checked={form.containersWithinReach === "n/a"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">N/A</span>
                </label>
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                If all containers are not within reach of vessel's gear, state self sustained capacity of gear:
              </label>
              <input
                disabled={readonly}
                type="text"
                name="ifNotWithinReachCapacity"
                value={form.ifNotWithinReachCapacity}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                If vessel fitted with all permanent and loose fittings/lashings materials for above number of TEU/FEU?:
              </label>
              <input
                disabled={readonly}
                type="text"
                name="fittingsForTEUFEU"
                value={form.fittingsForTEUFEU}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                Is vessel fitted with recessed holes/shoes on tanktop and container shoes on weatherdeck and hatch
                covers?:
              </label>
              <input
                disabled={readonly}
                type="text"
                name="recessedHolesShoes"
                value={form.recessedHolesShoes}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                Advise stack weights and number of tiers on/underdeck per TEU:
              </label>
              <input
                disabled={readonly}
                type="text"
                name="stackWeightsTEU"
                value={form.stackWeightsTEU}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                Advise stack weights and number of tiers on/underdeck per FEU:
              </label>
              <input
                disabled={readonly}
                type="text"
                name="stackWeightsFEU"
                value={form.stackWeightsFEU}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Has vessel a container spreader on board?:</label>
              <div className="mt-1 flex space-x-2">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="hasContainerSpreader"
                    value="yes"
                    checked={form.hasContainerSpreader === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="hasContainerSpreader"
                    value="no"
                    checked={form.hasContainerSpreader === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="hasContainerSpreader"
                    value="n/a"
                    checked={form.hasContainerSpreader === "n/a"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">N/A</span>
                </label>
              </div>
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("hasContainerSpreaderComments")}
                >
                  Comments
                </button>
              </label>
              {(form.hasContainerSpreaderComments || isCommentsVisible("hasContainerSpreaderComments")) && (
                <textarea
                  disabled={readonly}
                  name="hasContainerSpreaderComments"
                  value={form.hasContainerSpreaderComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Number and type of reefer plugs:</label>
              <input
                disabled={readonly}
                type="text"
                name="numberReeferPlugs"
                value={form.numberReeferPlugs}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          <br />
        </form>
      </div>
    </LoadingWrapper>
  );
};
